import { colorBorderSecondary, colorGray } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  getForbiddenErrorCode,
  isNotFound,
  isObject,
  Padding,
  TryAgainButton,
} from '@atlas-frontend/sdk';
import { Button, Card, Flex, Result } from 'antd';
import { FC, PropsWithChildren } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import * as Icon from 'react-feather';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Forbidden } from '../pages/Forbidden';
import { HttpErrorDisplayTemplate } from './HttpErrorDisplayTemplate';
import { InternalErrorDisplay } from './InternalErrorDisplay';
import translations from './AppErrorBoundary.translations.json';
import { colorRed } from './tokens';
import errorImg from '../assets/img/find-on-page.svg';

const { addTranslations, useTranslation } = i18nNsFactory('app-error-boundary');
addTranslations(translations);

export const AppErrorBoundaryFallback: FC<FallbackProps> = (props) => {
  const { error } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  var forbiddenErrorCode = getForbiddenErrorCode(error);
  if (forbiddenErrorCode) {
    return <Forbidden variant={forbiddenErrorCode} />;
  }

  if (isNotFound(error)) {
    return (
      <HttpErrorDisplayTemplate imgSrc={errorImg} title={t('not-found-title')}>
        {t('not-found-text')}
      </HttpErrorDisplayTemplate>
    );
  }

  return (
    <Wrapper>
      <StyledCard bordered={false}>
        <Result
          status="error"
          title={t('app-error-title')}
          subTitle={t('app-error-text')}
          icon={<Icon.XCircle size={80} color={colorRed} />}
        />
        <InternalErrorDisplay error={error} />
        <Footer horizontal vertical={10}>
          <Flex gap="small" justify="end">
            {(isObject(error) && error.actions) || (
              <>
                <TryAgainButton />
                <Button id="reload" type="primary" onClick={() => navigate(0)}>
                  {t('button.reload')}
                </Button>
              </>
            )}
          </Flex>
        </Footer>
      </StyledCard>
    </Wrapper>
  );
};

const AppErrorBoundary: FC<PropsWithChildren> = ({ children }) => (
  <ErrorBoundary
    fallbackRender={(fallbackProps) => (
      <AppErrorBoundaryFallback {...fallbackProps} />
    )}
  >
    {children}
  </ErrorBoundary>
);

export default AppErrorBoundary;

const Wrapper = styled.div`
  display: grid;
  place-items: center;
  height: 100%;
  width: 100%;
  border-left: 1px solid ${colorBorderSecondary};
`;

const StyledCard = styled(Card)`
  width: calc(100% - 2rem);
  max-width: 800px;
  box-shadow:
    0 9px 28px 8px #0000000d,
    0 6px 16px 0 #00000014,
    0 3px 6px -4px #0000001f !important;

  .ant-card-body {
    padding: 0;
  }
`;

const Footer = styled(Padding)`
  border-top: 1px solid ${colorGray[1]};
`;
